import { formatItemWithDescription } from "@components/inputs/select/Select.utils";
import { SwitchType } from "@components/inputs/switch/Switch";
import { IFieldDef, IGetValueArgs } from "@components/smart/FieldInfo";
import { createPath } from "@odata/BindingContext";
import {
    DocumentBusinessPartnerEntity,
    DocumentEntity,
    DocumentItemVatClassificationSelectionEntity,
    DocumentVatClassificationEntity,
    DocumentVatClassificationSelectionEntity,
    EntityTypeName,
    ICorrectiveInvoiceIssuedEntity,
    IDocumentEntity,
    IVatOssEntity,
    ProformaInvoiceIssuedEntity,
    VatClassificationEntity
} from "@odata/GeneratedEntityTypes";
import { CountryCode, DocumentTypeCode, SelectionCode, VatOssCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { IDocumentExtendedEntity } from "@pages/documents/DocumentInterfaces";
import {
    hasEverBeenOrWillBeOssCompany,
    isOssAvailable,
    isOssDocumentCallback,
    isOssMode,
    isOssModeCallback,
    IsOssModePath,
    OssAvailableDocTypes
} from "@pages/documents/Oss.global.utils";
import { IDefinition, TFieldsDefinition } from "@pages/PageUtils";
import { isNewEntity } from "@utils/definition.utils";
import i18next from "i18next";

import { DASH_CHARACTER } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType } from "../../enums";
import { TValue } from "../../global.types";
import { FormStorage } from "../../views/formView/FormStorage";

export const setIsOssModeBasedOnDocument = (storage: FormStorage<IDocumentExtendedEntity>): void => {
    if (!isOssAvailable(storage, storage.data.entity.DocumentTypeCode as DocumentTypeCode)) {
        return;
    }

    if (storage.data.entity.DocumentTypeCode === DocumentTypeCode.CorrectiveInvoiceIssued) {
        const correctedDocs = (storage.data.entity as ICorrectiveInvoiceIssuedEntity)?.CorrectedDocuments;
        // when new corrective invoice is created, set oss mode to off, until we know the corrected document is actually in oss mode
        let isOssMode = false;
        if (!!correctedDocs?.length) {
            // check if all corrected documents are in oss mode as it is not possible to make oss correction for non-oss document
            isOssMode = correctedDocs.every(correctedDoc => {
                return !!correctedDoc.VatClassificationSelection?.VatClassification?.VatOssCode;
            });
        }
        storage.setValueByPath(IsOssModePath, isOssMode);
        return;
    }

    const document = storage.data.entity;
    const isNew = isNewEntity({ storage });
    const hasSavedDraft = !!document.DocumentDraft?.Id;

    if (isNew && !hasSavedDraft) {
        // default OSS for new documents as default "Electronic service", but just for documents, which are actually created.
        // Once draft is saved, we need to respect the value from the VatClassification->VatOssCode
        const bc = storage.data.bindingContext
            .navigate(DocumentEntity.VatClassificationSelection)
            .navigate(DocumentVatClassificationSelectionEntity.VatClassification)
            .navigate(DocumentVatClassificationEntity.VatOss);
        storage.setValue(bc, VatOssCode.ElektronickáSlužba);
        storage.setValueByPath(IsOssModePath, true);
    } else {
        storage.setValueByPath(IsOssModePath, !!document?.VatClassificationSelection?.VatClassification?.VatOssCode);
    }
};

export const getOssSwitchDefinition = (): IFieldDef => {
    return {
        id: IsOssModePath,
        type: FieldType.Switch,
        label: i18next.t("Document:Form.IsOss"),
        fieldSettings: {
            type: SwitchType.YesNo
        },
        customizationData: {
            dependents: [`VatClassificationSelection/VatClassification/VatOss`]
        },
        defaultValue: (args: IGetValueArgs) => {
            return !!hasEverBeenOrWillBeOssCompany(args.storage.context);
        },
        isVisible: isOssModeCallback,
        isDisabled: (args: IGetValueArgs) => {
            const document = args.storage.getEntity<IDocumentExtendedEntity>();

            return document.DocumentTypeCode === DocumentTypeCode.CorrectiveInvoiceIssued;
        },
        backendPath: DocumentEntity.VatClassificationSelection
    };
};

export const getOssFieldsDefinition = (): TFieldsDefinition => {
    const prefix = "VatClassificationSelection/VatClassification/";

    const vatOssDef: Partial<IFieldDef> = {
        type: FieldType.ComboBox,
        isVisible: isOssDocumentCallback,
        fieldSettings: {
            displayName: "Name"
        }
    };

    return {
        [IsOssModePath]: getOssSwitchDefinition(),
        [`${prefix}VatOss`]: {
            ...vatOssDef,
            width: BasicInputSizes.M,
            label: i18next.t("Document:Form.VatOssDefault"),
            defaultValue: VatOssCode.ElektronickáSlužba,
            affectedFields: [
                { id: `Items/${prefix}VatOss` }
            ]
        },
        [`Items/${prefix}VatOss`]: {
            ...vatOssDef,
            label: i18next.t("Document:Form.VatOssItem"),
            width: BasicInputSizes.L,
            defaultValue: null,
            fieldSettings: {
                ...vatOssDef.fieldSettings,
                additionalItems: [
                    {
                        id: null,
                        label: i18next.t("Document:AccountAssignment.Default"),
                        groupId: "Default",
                        additionalData: { SelectionCode: SelectionCode.Default }
                    }
                ]
            },
            formatter: (val: TValue, args?: IFormatOptions) => {
                const isDefault = !val;

                if (isDefault) {
                    const vatOssCode = (args.entity as IDocumentEntity)?.VatClassificationSelection?.VatClassification?.VatOssCode;

                    return formatItemWithDescription(getEnumDisplayValue(EntityTypeName.VatOss, vatOssCode), i18next.t("Common:General.Default"));
                }

                const vatOss = args.item as IVatOssEntity;

                return getEnumDisplayValue(EntityTypeName.VatOss, vatOss?.Code);
            }
        }
    };
};

export const addOssTableDef = (definition: IDefinition, context: IAppContext, documentType: DocumentTypeCode): void => {
    if (!OssAvailableDocTypes.includes(documentType)) {
        return null;
    }
    const table = definition.table;
    const filterBarDef = table.filterBarDef;
    const path = createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.CountryCode);

    table.columnDefinition[path] = {
        label: i18next.t("Document:Table.Oss"),
        descriptionPath: createPath(DocumentEntity.VatClassificationSelection, DocumentVatClassificationSelectionEntity.VatClassification),
        formatter: (val: TValue, args?: IFormatOptions) => {
            const doc = args.entity as IDocumentEntity;

            if (!doc?.VatClassificationSelection?.VatClassification?.VatOssCode || doc.BusinessPartner?.CountryCode === CountryCode.CzechRepublic) {
                return DASH_CHARACTER;
            }

            return val as string;
        },
        additionalProperties: [
            { id: `/${createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.CountryCode)}` },
            { id: `/${createPath(DocumentEntity.VatClassificationSelection, DocumentVatClassificationSelectionEntity.VatClassification, DocumentVatClassificationEntity.VatOssCode)}` }
        ]
    };
    filterBarDef[0].filterDefinition[path] = {
        label: i18next.t("Document:Table.Oss"),
        descriptionPath: createPath(DocumentEntity.VatClassificationSelection, DocumentVatClassificationSelectionEntity.VatClassification)
    };

    // only keep in customization, don't add by default for now
    // const attachmentsIndex = table.columns.findIndex(col => col === DocumentEntity.Attachments);
    //
    // table.columns.splice(attachmentsIndex - 1, 0, path);
};

export const setIsOssModeDefaultsBasedOnDocument = (storage: FormStorage<IDocumentExtendedEntity>, clearingDate: Date): void => {
    if (isOssMode(storage, clearingDate)) {
        storage.setValueByPath(IsOssModePath, true);
        storage.setDefaultValueByPath(createPath(ProformaInvoiceIssuedEntity.VatClassificationSelection, DocumentItemVatClassificationSelectionEntity.VatClassification, VatClassificationEntity.VatOss));
        for (const item of storage.data.entity.Items) {
            item.Vat = null;
            item.VatCode = null;
            item.TransactionAmountNet = item.TransactionAmount;
            item.TransactionAmountVat = 0;
            item.TransactionUnitPriceNet = item.TransactionUnitPrice;
            item.TransactionUnitPriceVat = 0;
            item.VatClassificationSelection = { SelectionCode: SelectionCode.Default };
        }
    }
};
